import React, { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { InvestmentContextFull } from "../investment-process-full";

import { MultistepFormButtonsV2 } from "../../../../components/vertical-multistep-form/multistep-form-buttons";
import { AppContext } from "../../direct-router";
import API_CLIENT from "../../../../utility/api-client";
import {
  Amount,
  CURRENCY,
  Investment,
  Voucher,
} from "../../../../types/investment_types";
import { useHistory } from "react-router-dom";

import AmountUtils from "../../../../utility/amount-utils";
import { Text, Card, Divider, Button, Input, Spacer, useTheme } from "@geist-ui/core";

import { ChevronRight, Info } from "@geist-ui/icons";

type SummaryProps = {
  back: () => void;
  next: () => void;
};

// Styles copied from geist-form-inputs.tsx for consistency
const inputBaseStyle: React.CSSProperties = {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    border: '1px solid #D4D4D4',
    height: '56px',
    fontSize: '16px',
    color: '#000',
    width: '100%',
    padding: '0 20px',
    transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    outline: 'none',
    fontFamily: 'inherit'
};

const inputFocusStyle: React.CSSProperties = {
    borderColor: '#000',
    borderWidth: '1px',
    backgroundColor: '#fff',
    boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
    transform: 'scale(1.005) translateY(-2px)',
};

export default function Summary({ back, next }: SummaryProps) {
  const investment_context = useContext(InvestmentContextFull);
  const app_context = useContext(AppContext);
  const investment = investment_context.investment;
  const history = useHistory();
  const theme = useTheme();

  const [discount_code, setDiscountCode] = useState("");
  const [voucher, setVoucher] = useState<Voucher>();
  const [disagio, setDisagio] = useState<Amount>();
  const [showVoucherInput, setShowVoucherInput] = useState(false);

  useEffect(() => {
    //affiliate program
    let affiliate_program = app_context.user?.marketing?.affiliate_program;
    if (affiliate_program == "FINANCEMATE") {
      var investment_amount = investment?.amount;
      if (!investment_amount) return;
      var discount_total = (investment_amount * 5).toString();
      const five_percent_discount_amount: Amount = {
        amount: discount_total,
        decimals: 2,
        currency: CURRENCY.EUR,
      };
      setDisagio(five_percent_discount_amount);
    }
  }, []);

  useEffect(() => {
    if (voucher == undefined) return;
    if (voucher.valid == false) {
      setDisagio(undefined);
      return;
    }

    const voucher_amount_off = voucher.discount.amount_off;
    const voucher_percent_off = voucher.discount.percent_off;

    var investment_amount = investment?.amount;
    if (!investment_amount) return;
    var discount_total = "0";
    if (voucher_amount_off) {
      discount_total = (Number(voucher_amount_off) * 100).toString();
    } else if (voucher_percent_off) {
      discount_total = (
        investment_amount * Number(voucher_percent_off)
      ).toString();
    } else {
      discount_total = "0";
    }

    var disagio_obj = {
      amount: discount_total,
      decimals: 2,
      currency: CURRENCY.EUR,
    };

    setDisagio(disagio_obj);
  }, [voucher]);

  const submitInvestment = () => {
    window.scrollTo(0, 0);
    if (investment === null) return;
    investment_context.setLoading(true);
    API_CLIENT.makeBindingOfferV3(
      app_context.token,
      investment.id,

      (investment: Investment) => {
        (window as any).dataLayer.push({
          event: "investment-accepted",
          user_id: app_context.sub,
          "investment-amount": investment.amount,
        });
        investment_context.setInvestment(investment);
        history.push("/direct/dashboard?investment_id=" + investment.id);

        localStorage.setItem(`investment-amount-${investment.campaignId}`, "");
      },
      (error: string) => {
        console.log(error);
      },
      voucher?.code
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitInvestment();
  };

  if (!investment) return null;

  const raw_agio = investment_context.campaign?.agio ?? 0.05;
  const agio_rate = raw_agio >= 1 ? raw_agio / 100 : raw_agio; 
  const agio_percent = raw_agio >= 1 ? raw_agio : raw_agio * 100;
  
  let agio_val = 0;
  if (investment.agio) {
    agio_val = Number(investment.agio.amount) / Math.pow(10, 2);
  }

  let disagio_val = 0;
  if (disagio) {
    disagio_val = Number(disagio.amount) / Math.pow(10, 2);
  }

  const base_amount = investment_context.amount || 0;
  const total_sum = base_amount + agio_val - disagio_val;

  // Common card style for consistency
  const sectionCardStyle = {
      borderRadius: '16px',
      border: '1px solid #EAEAEA',
      boxShadow: 'none',
      padding: 'clamp(16px, 3vw, 24px)',
      marginBottom: '1.5rem',
      backgroundColor: '#FFFFFF',
      width: '100%'
  };

  const sectionTitleStyle = {
      fontFamily: '"Nunito Sans", sans-serif',
      fontSize: 'clamp(16px, 4vw, 18px)',
      fontWeight: 700,
      marginTop: 0,
      marginBottom: '1.5rem'
  };

  return (
    <div className="flex flex-col items-start justify-start min-h-screen w-full max-w-4xl pb-24">
      <div className="flex flex-col gap-4 mb-2">
        <Text h3 style={{ margin: 0, fontSize: 'clamp(20px, 5vw, 24px)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
          Zusammenfassung
        </Text>
        <Text p type="secondary" style={{ margin: 0, fontSize: 'clamp(14px, 4vw, 16px)', lineHeight: 1.5 }}>
          Überprüfen Sie Ihre Angaben ein letztes Mal, bevor Sie Ihre Investition verbindlich unterzeichnen.
        </Text>
      </div>

      <Card width="100%" style={sectionCardStyle}>
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <Text span type="secondary" style={{ fontSize: '14px' }}>Anlagebetrag</Text>
            <Text b style={{ fontSize: '16px' }}>{base_amount.toLocaleString("de-DE")} €</Text>
          </div>
          
          <div className="flex justify-between items-center">
             <Text span type="secondary" style={{ fontSize: '14px' }}>Agio ({agio_percent}%)</Text>
             <Text span style={{ fontSize: '16px' }}>{AmountUtils.toString(investment.agio)}</Text>
          </div>

          {disagio && (
            <div className="flex justify-between items-center text-green-600">
               <Text span style={{ fontSize: '14px', color: 'inherit' }}>Rabatt / Gutschein</Text>
               <Text b style={{ fontSize: '16px', color: 'inherit' }}>- {AmountUtils.toString(disagio)}</Text>
            </div>
          )}

          <Divider style={{ margin: '0.5rem 0' }} />

          <div className="flex justify-between items-end">
            <Text b style={{ fontSize: '18px' }}>Gesamtbetrag</Text>
            <Text b style={{ fontSize: 'clamp(20px, 5vw, 24px)', letterSpacing: '-0.02em' }}>
              {total_sum.toLocaleString("de-DE")} €
            </Text>
          </div>
        </div>
      </Card>
      <Spacer h={2.5} />

      <div className="w-full">
         {!showVoucherInput ? (
           <Text 
             small 
             span 
             onClick={() => setShowVoucherInput(true)} 
             style={{ 
               cursor: 'pointer', 
               color: '#888', 
               textDecoration: 'underline', 
               textUnderlineOffset: '4px',
               fontSize: '12px',
               fontWeight: 500
             }}
           >
             Ich habe einen Investitionscode
           </Text>
         ) : (
           <motion.div 
             initial={{ opacity: 0, y: -10 }}
             animate={{ opacity: 1, y: 0 }}
             className="flex flex-col gap-3"
           >
              <Text small span type="secondary" style={{ textTransform: 'uppercase', fontWeight: 700, fontSize: '11px', letterSpacing: '0.05em', display: 'block' }}>
                 Haben Sie einen Investitionscode?
              </Text>
              <div className="flex flex-col sm:flex-row gap-3">
                 <div className="flex-1">
                     {/* Used a standard input here instead of Geist Input to fully control the style */}
                     <input 
                         className="w-full"
                         placeholder="Code eingeben"
                         value={discount_code}
                         onChange={(e) => setDiscountCode(e.target.value)}
                         style={inputBaseStyle}
                         onFocus={(e) => {
                             Object.assign(e.currentTarget.style, inputFocusStyle);
                         }}
                         onBlur={(e) => {
                             e.currentTarget.style.borderColor = voucher?.valid === false ? theme.palette.error : '#D4D4D4';
                             e.currentTarget.style.borderWidth = '1px';
                             e.currentTarget.style.backgroundColor = '#FFFFFF';
                             e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
                             e.currentTarget.style.transform = 'scale(1) translateY(0)';
                         }}
                     />
                 </div>
                 <motion.button 
                     whileHover={{ scale: 1.02 }}
                     whileTap={{ scale: 0.98 }}
                     onClick={() => {
                         var c_id = investment_context.campaign?.cashlinkId;
                         if (c_id == null) return;
                         API_CLIENT.validateVoucherV2(
                           app_context.token,
                           discount_code,
                           c_id,
                           (voucher) => {
                             setVoucher(voucher);
                           },
                           (error) => {
                             setDisagio(undefined);
                             setVoucher(undefined);
                           }
                         );
                     }}
                     className="px-6 py-2.5 rounded-full bg-black text-white font-bold text-sm uppercase tracking-tight h-[56px]"
                 >
                     Check
                 </motion.button>
              </div>
           </motion.div>
         )}
      </div>

      <Spacer h={4} />

      <div className="mt-8 sm:mt-12 pt-8 border-t border-gray-100 flex flex-col items-center justify-between gap-8 md:flex-row w-full">
            <div className="flex items-start gap-4 max-w-[500px] w-full bg-gray-50 p-6 rounded-2xl border border-gray-100">
               <div className="mt-0.5 flex-shrink-0">
                  <Info size={18} color="#666" />
               </div>
               <Text small style={{ color: '#666', lineHeight: 1.6, margin: 0, fontSize: '13px', fontWeight: 500 }}>
                 Hinweis: Sie gehen noch keine Verpflichtung ein. Die rechtlich bindende Zeichnung erfolgt erst im nächsten Schritt durch Ihre finale Bestätigung.
               </Text>
            </div>
            
            <div className="w-full md:w-auto">
               <form onSubmit={handleSubmit} className="w-full">
                   <MultistepFormButtonsV2
                       back={true}
                       backFunction={back}
                       nextButtonText="Bestätigen"
                       disabled={false}
                   />
               </form>
            </div>
      </div>
    </div>
  );
}
